import { Component, Input, OnDestroy } from '@angular/core';
import camelCase from 'lodash/camelCase';
import upperFirst from 'lodash/upperFirst';
import { Subscription } from 'rxjs';

import { ProjectsService } from '../projects.service';
import { Project } from '../../shared/models/project.model';
import { User } from '../../shared/models/user.model';
import { TranslationBundle, TranslationService } from '../../translation.service';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { FwAvatarModule, FwButtonModule, FwButtonToggleModule, FwCardModule, FwContainedInputModule, FwIconButtonModule, FwIconModule, FwLayoutsModule, FwMenuModule, FwSectionHeadingModule, FwSelectMenuModule, FwSwitchModule, FwTextInputModule } from '@flywheel-io/vision';
import { AdminModule } from 'app/src/admin/admin.module';
import { DirectivesModule } from 'app/src/common/directives.module';
import { ContainerModule } from 'app/src/containers/container.module';
import { FileModule } from 'app/src/files/file.module';
import { MaterialModule } from 'app/src/material.module';
import { ProjectsImportsModule } from 'app/src/project-imports/project-imports.module';
import { SessionModule } from 'app/src/sessions/session.module';
import { PipesModule } from 'app/src/shared/pipes.module';
import { SharedModule } from 'app/src/shared/shared.module';

@Component({
  host: { 'test-id': 'project-lock-status' },
  selector: 'project-lock-status',
  styleUrls: ['./project-lock-status.component.scss'],
  template: `
    <ng-container *ngIf="project?.locked && project?._id">
      <span #lockStatus> <mat-icon test-id="project-lock-icon"  class="locked xs">lock</mat-icon></span>
      <fw-popover [trigger]="lockStatus" position="below" action="mouseenter">
        <div class="lock-status padding-15">
          <strong class="title">{{ 'projectLockStatus.title.locked' | translate }}</strong>
          <strong test-id="project-lock-reason">{{ 'projectLockStatus.reason' | translate }}</strong>
          <span>{{ reason }}</span>
          <strong test-id="project-lock-timestamp">{{ 'projectLockStatus.timestamp' | translate }}</strong>
          <span>{{ project.locked.timestamp | date:('projectLocking.lockStatus.timeFormat' | translate) }}</span>
          <strong>{{ 'projectLockStatus.lockedBy' | translate }}</strong>
          <span test-id="project-lock-user">
            <app-avatar [users]="user" [enablePopover]="false"></app-avatar>
            <span>{{ user?.firstname }} {{ user?.lastname }} &lt;{{ user?.email }}&gt;</span>
          </span>
          <a test-id="view-settings-button" mat-button color="primary" class="light-primary-background"
            href="#/projects/{{ project?._id }}/settings/lock">
            {{ 'projectLockStatus.viewSettingsButton.locked' | translate }}
          </a>
        </div>
      </fw-popover>
    </ng-container>
    <ng-container *ngIf="!project?.locked && project?._id">
        <span #unlockedStatus>
          <mat-icon test-id="project-lock-icon" class="unlocked xs">lock_open</mat-icon>
        </span>
      <fw-popover [trigger]="unlockedStatus" position="below" action="mouseenter">
        <div class="lock-status padding-15">
          <strong class="title">{{ 'projectLockStatus.title.unlocked' | translate }}</strong>
          <a test-id="view-settings-button" mat-button color="primary" class="light-primary-background"
            href="#/projects/{{ project._id }}/settings/lock">
            {{ 'projectLockStatus.viewSettingsButton.unlocked' | translate }}
          </a>
        </div>
      </fw-popover>
    </ng-container>`,
  standalone: true,
  imports: [
    AdminModule,
    CommonModule,
    ContainerModule,
    DirectivesModule,
    FileModule,
    FormsModule,
    FwAvatarModule,
    FwButtonModule,
    FwButtonToggleModule,
    FwCardModule,
    FwContainedInputModule,
    FwIconButtonModule,
    FwIconModule,
    FwLayoutsModule,
    FwMenuModule,
    FwSectionHeadingModule,
    FwSelectMenuModule,
    FwSwitchModule,
    FwTextInputModule,
    MaterialModule,
    PipesModule,
    ProjectsImportsModule,
    ReactiveFormsModule,
    ScrollingModule,
    SessionModule,
    SharedModule,
    RouterModule,
  ],
})
export class ProjectLockStatusComponent implements OnDestroy {
  @Input() project: Project

  private messages = {
    lockingReasons: TranslationBundle.EMPTY,
  }
  private subscriptions = {
    messages: Subscription.EMPTY,
    users: Subscription.EMPTY,
  }
  private users: Record<string, User> = {}

  constructor(
    private projectsService: ProjectsService,
    private translate: TranslationService,
  ) {
    this.subscriptions.messages = this.translate.getBundle('projectLocking.lockReasons').subscribe(lockingReasons => {
      this.messages = { lockingReasons };
    });
    this.subscriptions.users = this.projectsService.getUsers().subscribe(users => {
      this.users = Object.assign({}, ...users.map(user => ({ [user._id]: user })));
    });
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  get user(): User {
    return this.users[this.project?.locked?.origin.id];
  }

  get reason(): string {
    const key = this.project?.locked?.reason;
    if (!key) {
      return '';
    }
    return this.messages.lockingReasons.get(camelCase(key), true) ?? upperFirst(key.replace(/_/g, ' '));
  }
}
